/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faQuestionCircle, 
  faChevronDown,
  faChevronUp,
  faUser,
  faBriefcase,
  faBuilding,
  faCog,
  faShieldAlt,
  faEnvelope,
  faPhone,
  faComments,
  faBook,
  faVideo,
  faLifeRing,
  faHome,
  faSpinner,
  faUsers,
  faHandsHelping,
  faUniversity,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import '../styles/HelpCenter.css';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const faqCategories = [
    { id: 'all', name: 'All Topics', icon: faBook },
    { id: 'account', name: 'Account & Profile', icon: faUser },
    { id: 'jobs', name: 'Job Search', icon: faBriefcase },
    { id: 'employers', name: 'For Employers', icon: faBuilding },
    { id: 'communities', name: 'Communities', icon: faUsers },
    { id: 'volunteers', name: 'Volunteers', icon: faHandsHelping },
    { id: 'universities', name: 'Universities', icon: faUniversity },
    { id: 'technical', name: 'Technical Issues', icon: faCog },
    { id: 'privacy', name: 'Privacy & Security', icon: faShieldAlt }
  ];

  // Default FAQs as fallback
  const defaultFaqs = [
    {
      id: 1,
      category: 'account',
      question: 'How do I create an account on AksharJobs?',
      answer: 'Click "Sign Up" on the homepage, choose your role (Job Seeker or Recruiter), fill in your details, and verify your email address. You can also sign up using Google or LinkedIn for faster registration.'
    },
    {
      id: 2,
      category: 'account',
      question: 'How do I update my profile information?',
      answer: 'Go to your dashboard and click "Edit Profile" or "Edit Personal Info" in any section. You can update your contact information, skills, experience, education, and projects directly from your profile page.'
    },
    {
      id: 3,
      category: 'jobs',
      question: 'How does the AI job matching work?',
      answer: 'Our AI analyzes your resume, skills, experience, and preferences to match you with relevant job opportunities. The system uses advanced algorithms to calculate match scores based on job requirements and your qualifications.'
    },
    {
      id: 4,
      category: 'jobs',
      question: 'Can I apply to jobs without creating an account?',
      answer: 'You can browse public job listings, but you need to create an account to apply for jobs, save positions, and access personalized job recommendations.'
    },
    {
      id: 5,
      category: 'employers',
      question: 'How do I post a job on AksharJobs?',
      answer: 'After creating a recruiter account, go to your dashboard and click "Post a Job". Fill in the job details, requirements, and company information. Your job will be reviewed and published within 24 hours.'
    },
    {
      id: 6,
      category: 'account',
      question: 'How can I complete my profile?',
      answer: 'To complete your profile, go to your dashboard and click on "My Profile" or "Edit Profile". Fill in all sections including personal information, skills, work experience, education, and projects. A complete profile increases your visibility to employers and improves job matching accuracy. You can save your progress at any time and return to complete it later.'
    },
    {
      id: 7,
      category: 'technical',
      question: 'The website is loading slowly. What should I do?',
      answer: 'Try refreshing the page, clearing your browser cache, or switching to a different browser. If you\'re on mobile, ensure you have a stable internet connection.'
    },
    {
      id: 8,
      category: 'privacy',
      question: 'How is my personal data protected?',
      answer: 'We use enterprise-grade security measures including SSL encryption, secure servers, and strict data access controls. We never share your personal information without your explicit consent.'
    },
    {
      id: 9,
      category: 'privacy',
      question: 'Can I delete my account and data?',
      answer: 'Yes, you can delete your account anytime from your profile settings. This will permanently remove all your data from our systems within 30 days.'
    },
    {
      id: 10,
      category: 'account',
      question: 'How do I verify my email address?',
      answer: 'After signing up, check your email inbox for a verification email from AksharJobs. Click the verification link in the email to activate your account. If you don\'t see the email, check your spam folder or request a new verification email from your account settings.'
    },
    {
      id: 11,
      category: 'account',
      question: 'I forgot my password. How do I reset it?',
      answer: 'Click on "Forgot Password" on the login page, enter your registered email address, and we\'ll send you a password reset link. Click the link in the email to create a new password. Make sure to use a strong, unique password for better security.'
    },
    {
      id: 12,
      category: 'jobs',
      question: 'How do I apply for a job?',
      answer: 'Browse available jobs, click on a job listing that interests you, review the details, and click the "Apply" button. Make sure your profile is complete before applying, as employers will review your profile information. You can track all your applications from your dashboard.'
    },
    {
      id: 13,
      category: 'jobs',
      question: 'How can I save jobs to apply later?',
      answer: 'When viewing a job listing, click the "Save" or bookmark icon to add it to your saved jobs list. You can access all saved jobs from your dashboard under the "Saved Jobs" section. This allows you to review and apply to jobs at your convenience.'
    },
    {
      id: 14,
      category: 'jobs',
      question: 'How do job alerts work?',
      answer: 'Set up job alerts by specifying your preferred job titles, locations, and other criteria. We\'ll notify you via email when new jobs matching your preferences are posted. You can manage your job alerts from your account settings.'
    },
    {
      id: 15,
      category: 'account',
      question: 'How can I make my profile more visible to employers?',
      answer: 'Complete all sections of your profile, add relevant skills, include a professional summary, and keep your work experience and education up to date. A complete profile with detailed information increases your chances of being discovered by recruiters and improves your job match scores.'
    },
    {
      id: 16,
      category: 'account',
      question: 'What should I include in my skills section?',
      answer: 'List both technical and soft skills relevant to your career. Include programming languages, tools, certifications, languages you speak, and soft skills like communication or leadership. Be specific and honest about your proficiency level. Regular updates help maintain profile accuracy.'
    },
    {
      id: 17,
      category: 'jobs',
      question: 'What happens after I apply for a job?',
      answer: 'After submitting your application, the employer will review your profile. You\'ll receive email notifications about your application status. You can track all applications, including pending, viewed, shortlisted, or rejected statuses, from your dashboard application tracker.'
    },
    {
      id: 18,
      category: 'employers',
      question: 'Is AksharJobs free for employers?',
      answer: 'Yes! AksharJobs is completely free for all users, including employers and recruiters. You can post jobs, browse candidate profiles, and use all platform features at no cost. We believe in providing equal opportunities for everyone.'
    },
    {
      id: 19,
      category: 'employers',
      question: 'How do I search for candidates?',
      answer: 'Use the candidate search feature in your recruiter dashboard. Filter candidates by skills, experience, location, education, and other criteria. You can save candidate profiles, send messages, and manage your candidate pipeline all in one place.'
    },
    {
      id: 20,
      category: 'technical',
      question: 'Which browsers are supported?',
      answer: 'AksharJobs works best on modern browsers including Google Chrome, Mozilla Firefox, and Microsoft Edge. Make sure your browser is updated to the latest version for the best experience. For mobile users, the platform is optimized for Android browsers. Currently, Safari and iOS devices are not supported.'
    },
    {
      id: 21,
      category: 'technical',
      question: 'I\'m having trouble logging in. What should I do?',
      answer: 'First, verify you\'re using the correct email and password. Try clearing your browser cache and cookies, or use an incognito/private window. If the issue persists, use the "Forgot Password" option to reset your password, or contact our support team for assistance.'
    },
    {
      id: 22,
      category: 'account',
      question: 'Can I change my account type after registration?',
      answer: 'Once you create an account as a Job Seeker or Recruiter, you cannot change the account type from the same account. However, you can create a separate account with a different email address for a different role if needed.'
    },
    {
      id: 23,
      category: 'jobs',
      question: 'How do I prepare for interviews through AksharJobs?',
      answer: 'Access our Career Resources Hub for interview preparation guides, tips, and checklists. Review the job requirements thoroughly, research the company, prepare questions to ask, and practice common interview questions. Our AI career advisor can also provide personalized interview tips based on your profile.'
    },
    {
      id: 24,
      category: 'privacy',
      question: 'Who can see my profile information?',
      answer: 'Your profile visibility depends on your privacy settings. Recruiters and employers can view your profile when you apply to their jobs. You can control what information is visible in your account settings. We never share your contact information without your permission.'
    },
    {
      id: 25,
      category: 'account',
      question: 'How do notifications work?',
      answer: 'You\'ll receive email notifications for job matches, application updates, messages from employers, and important account activities. You can customize your notification preferences in your account settings to control what notifications you receive and how often.'
    },
    {
      id: 26,
      category: 'jobs',
      question: 'Can I apply for multiple jobs at once?',
      answer: 'Yes, you can apply to as many jobs as you want. However, we recommend tailoring each application and only applying to positions that genuinely match your skills and interests. Quality applications are more effective than quantity.'
    },
    {
      id: 27,
      category: 'employers',
      question: 'How long does it take to review and publish a job posting?',
      answer: 'Job postings are typically reviewed and published within 24 hours of submission. Once approved, your job will be visible to all job seekers on the platform. You can edit or remove job postings at any time from your dashboard.'
    },
    {
      id: 28,
      category: 'account',
      question: 'What is the difference between Job Seeker and Intern profiles?',
      answer: 'Job Seeker profiles are for professionals seeking full-time or part-time employment. Intern profiles are specifically designed for students and recent graduates looking for internship opportunities, with features tailored to showcase academic achievements and learning goals.'
    },
    {
      id: 29,
      category: 'communities',
      question: 'What are Communities on AksharJobs?',
      answer: 'Communities are groups of professionals who share common interests, industries, or goals. They provide a platform for networking, knowledge sharing, collaboration, and mutual support. You can join existing communities or create your own to connect with like-minded professionals.'
    },
    {
      id: 31,
      category: 'communities',
      question: 'Can I create my own community?',
      answer: 'Yes! If you have a specific interest, industry focus, or goal that isn\'t represented by existing communities, you can bring your own community to AksharJobs. To create a new community, please contact the AksharJobs help team through our support channels. They will guide you through the process and help set up your community with the appropriate purpose, rules, and guidelines.'
    },
    {
      id: 32,
      category: 'communities',
      question: 'What can I do in a community?',
      answer: 'Communities offer various features including discussion forums, resource sharing, event announcements, job postings within the community, mentorship opportunities, and networking with professionals in your field. You can ask questions, share insights, collaborate on projects, and build meaningful professional relationships.'
    },
    {
      id: 33,
      category: 'communities',
      question: 'Are communities free to join?',
      answer: 'Yes, all communities on AksharJobs are completely free to join and participate in. There are no membership fees or charges for creating or managing communities. We believe in fostering free and open professional networking.'
    },
    {
      id: 34,
      category: 'volunteers',
      question: 'What is the Volunteers section on AksharJobs?',
      answer: 'The Volunteers section connects individuals who want to contribute their time and skills to meaningful causes with organizations and projects that need volunteer support. It\'s a platform for social impact, community service, and professional development through volunteering.'
    },
    {
      id: 35,
      category: 'volunteers',
      question: 'How do I find volunteer opportunities?',
      answer: 'Visit the Volunteers page to browse available volunteer opportunities. You can filter by cause, location, time commitment, and skills required. Each opportunity includes details about the organization, project description, time requirements, and how to apply.'
    },
    {
      id: 36,
      category: 'volunteers',
      question: 'Can I post volunteer opportunities as an organization?',
      answer: 'Yes! Organizations, NGOs, and community groups can post volunteer opportunities on AksharJobs. Create an account, go to the Volunteers section, and click "Post Volunteer Opportunity". Provide details about your project, required skills, time commitment, and how volunteers can contribute.'
    },
    {
      id: 37,
      category: 'volunteers',
      question: 'Do I need experience to volunteer?',
      answer: 'Not necessarily! Many volunteer opportunities welcome individuals with enthusiasm and willingness to learn. Some positions may require specific skills, but many are open to beginners. Volunteering is also a great way to gain experience and develop new skills while making a positive impact.'
    },
    {
      id: 38,
      category: 'volunteers',
      question: 'Can volunteering help my career?',
      answer: 'Absolutely! Volunteering can enhance your resume, help you develop new skills, expand your professional network, and demonstrate your commitment to social causes. Many employers value volunteer experience as it shows initiative, leadership, and community engagement. You can also gain references and recommendations from volunteer work.'
    },
    {
      id: 39,
      category: 'volunteers',
      question: 'Is there a time commitment required for volunteering?',
      answer: 'Time commitments vary by opportunity. Some projects require a few hours per week, while others may be one-time events or long-term commitments. Each volunteer opportunity clearly states the expected time commitment, so you can choose opportunities that fit your schedule.'
    },
    {
      id: 40,
      category: 'universities',
      question: 'What is the Universities section on AksharJobs?',
      answer: 'The Universities section connects educational institutions with students, job seekers, and employers. Universities can showcase their programs, post opportunities for students, connect with employers for internships and job placements, and engage with the professional community.'
    },
    {
      id: 41,
      category: 'universities',
      question: 'How can universities use AksharJobs?',
      answer: 'Universities can create profiles, post internship and job opportunities for their students, connect with employers for campus recruitment, share educational resources, and engage with alumni. The platform helps bridge the gap between academic institutions and the professional world.'
    },
    {
      id: 42,
      category: 'universities',
      question: 'Can students find opportunities through their university profile?',
      answer: 'Yes! Students can browse opportunities posted by their university, connect with employers who partner with their institution, access career resources, and network with alumni. Universities often post exclusive internships, research opportunities, and job openings for their students.'
    },
    {
      id: 43,
      category: 'universities',
      question: 'How do universities register on AksharJobs?',
      answer: 'Universities can register by creating an institutional account. Go to the sign-up page, select "University" as your account type, and provide your institution\'s details including official name, contact information, and verification documents. Once verified, you can start posting opportunities and connecting with students and employers.'
    },
    {
      id: 44,
      category: 'universities',
      question: 'Can employers partner with universities through AksharJobs?',
      answer: 'Yes! Employers can connect with universities to establish partnerships for internships, graduate programs, research collaborations, and campus recruitment. The platform facilitates these connections, making it easier for employers to find qualified candidates and for universities to provide career opportunities to their students.'
    },
    {
      id: 45,
      category: 'universities',
      question: 'Is there a cost for universities to use AksharJobs?',
      answer: 'No, AksharJobs is completely free for all users including universities. Educational institutions can post unlimited opportunities, connect with employers and students, and use all platform features at no cost. We believe in supporting education and career development for everyone.'
    }
  ];

  // Initialize with default FAQs, then try to fetch from API
  useEffect(() => {
    // Always start with default FAQs to ensure they're visible
    setFaqs(defaultFaqs);
    setLoading(true);
    
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(buildApiUrl('/api/admin/public/faqs'));
        
        // Use API FAQs only if they exist and are not empty
        // This allows admin-edited FAQs to be shown, but defaults remain as fallback
        if (response.data && 
            response.data.success && 
            Array.isArray(response.data.faqs) && 
            response.data.faqs.length > 0) {
          // Use database FAQs if they exist (admin may have edited them)
          setFaqs(response.data.faqs);
        }
        // If API returns empty or fails, keep using defaultFaqs (already set)
      } catch (error) {
        console.error('Error fetching FAQs from API, using default FAQs:', error);
        // Keep using defaultFaqs (already set above)
      } finally {
        setLoading(false);
      }
    };

    // Fetch FAQs from API (non-blocking - defaults are already shown)
    fetchFAQs();
  }, []);

  // Default videos (fallback when API returns empty or fails)
  const defaultFeaturedVideo = {
    id: 'featured',
    videoId: '',
    title: 'How AksharJobs Works',
    description: '2 minute platform overview',
    category: 'overview'
  };
  const defaultVideoTutorials = {
    jobSeeker: [
      { id: 'js2', videoId: '', title: 'Sign Up Guide', description: 'Create your account in minutes.' },
      { id: 'js3', videoId: '', title: 'Jobseeker Dashboard', description: 'How to manage your profile and applications.' },
      { id: 'js4', videoId: '', title: 'How to Apply for Jobs', description: 'Step-by-step application process.' }
    ],
    employer: [
      { id: 'emp0', videoId: '', title: 'Sign Up Guide', description: 'Create your employer account in minutes.' },
      { id: 'emp1', videoId: '', title: 'Employer Dashboard', description: 'Navigate your recruiter dashboard.' },
      { id: 'emp2', videoId: '', title: 'Post a Job', description: 'Create and publish job listings.' },
      { id: 'emp3', videoId: '', title: 'Create Company Profile', description: 'Set up your company page.' },
      { id: 'emp4', videoId: '', title: 'Manage Applicants', description: 'Review and manage job applications.' }
    ],
    intern: [
      { id: 'in1', videoId: '', title: 'Sign Up Guide', description: 'Create your intern account in minutes.' },
      { id: 'in2', videoId: '', title: 'Build Your Intern Profile', description: 'Complete your profile for internships.' },
      { id: 'in3', videoId: '', title: 'Find Internships', description: 'Discover and apply to internships.' }
    ]
  };

  const [featuredVideo, setFeaturedVideo] = useState(defaultFeaturedVideo);
  const [videoTutorials, setVideoTutorials] = useState(defaultVideoTutorials);
  const [videosLoading, setVideosLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(buildApiUrl('/api/admin/public/help-videos'));
        if (response.data?.success && Array.isArray(response.data.videos) && response.data.videos.length > 0) {
          const videos = response.data.videos;
          const overview = videos.filter((v) => v.category === 'overview').sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          const jobSeeker = videos.filter((v) => v.category === 'jobSeeker').sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          const employer = videos.filter((v) => v.category === 'employer').sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          const intern = videos.filter((v) => v.category === 'intern').sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          const featured = overview[0]
            ? { id: overview[0].id, videoId: overview[0].videoId || '', title: overview[0].title, description: overview[0].description || '', category: 'overview' }
            : defaultFeaturedVideo;
          setFeaturedVideo(featured);
          setVideoTutorials({
            jobSeeker: jobSeeker.map((v) => ({ id: v.id, videoId: v.videoId || '', title: v.title, description: v.description || '' })),
            employer: employer.map((v) => ({ id: v.id, videoId: v.videoId || '', title: v.title, description: v.description || '' })),
            intern: intern.map((v) => ({ id: v.id, videoId: v.videoId || '', title: v.title, description: v.description || '' }))
          });
        }
      } catch (err) {
        console.error('Error fetching help videos:', err);
      } finally {
        setVideosLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const hasVideoId = (id) => id && id.trim() !== '';

  const getEmbedUrl = (videoId) =>
    `https://www.youtube.com/embed/${videoId}?modestbranding=1&playsinline=1`;

  const supportOptions = [
    {
      title: 'Live Chat Support',
      description: 'Connect with our dedicated support specialists in real-time for immediate assistance with your inquiries',
      icon: faComments,
      availability: 'Mon-Fri 8AM-5PM, Sat 8AM-1PM EAT',
      responseTime: 'Instant Response',
      action: () => navigate('/contact-us')
    },
    {
      title: 'Email Support',
      description: 'Submit detailed inquiries and receive comprehensive responses from our expert support team',
      icon: faEnvelope,
      availability: 'Mon-Fri 8AM-5PM, Sat 8AM-1PM EAT',
      responseTime: 'Within 4 Hours',
      action: () => {
        window.location.href = 'mailto:info@aksharjobs.com?subject=Support Request';
      }
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with our customer success team for personalized assistance and guidance',
      icon: faPhone,
      availability: 'Mon-Fri 8AM-5PM, Sat 8AM-1PM EAT',
      responseTime: 'Immediate Connection',
      action: () => {
        window.location.href = 'tel:+254743599050';
      }
    },
    {
      title: 'Knowledge Base',
      description: 'Access our comprehensive library of tutorials, guides, and resources to find answers independently',
      icon: faVideo,
      availability: '24/7 Access',
      responseTime: 'Self-Service',
      action: () => navigate('/resources')
    }
  ];

  const categorySearchKeywords = {
    account: 'account profile signup sign up login password email verification jobseeker job seeker intern',
    jobs: 'job jobs jobsearch job search apply application applications alert alerts recommendation recommendations jobseeker job seeker',
    employers: 'employer employers recruiter recruiters hiring candidate candidates post job applicants',
    communities: 'community communities networking groups',
    volunteers: 'volunteer volunteers volunteering ngo social impact',
    universities: 'university universities campus student students internship internships education',
    technical: 'technical issue issues bug bugs error errors browser loading login',
    privacy: 'privacy security data protection account safety'
  };
  const videoRoleKeywords = {
    overview: 'overview how it works platform guide',
    jobSeeker: 'jobseeker job seeker candidate apply application applications jobs dashboard profile signup sign up',
    employer: 'employer employers recruiter recruiters hiring candidate candidates dashboard post job applicants',
    intern: 'intern interns internship internships student students profile apply'
  };

  const normalizeText = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /** Whole-word match so "employer" does not match inside "jobseeker". Case-insensitive via normalized text. */
  const tokenMatchesInNormalizedText = (normalizedMergedText, token) => {
    if (!token) return true;
    const padded = ` ${normalizedMergedText} `;
    if (token === 'jobseeker' || token === 'jobseekers') {
      return (
        /\bjob\s+seekers?\b/.test(padded) ||
        /\bjobseekers?\b/.test(padded)
      );
    }
    const re = new RegExp(`\\b${escapeRegex(token)}s?\\b`, 'i');
    return re.test(padded);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const normalizedSearchTokens = normalizeText(searchTerm).split(' ').filter(Boolean);

  const matchesSearch = (...values) => {
    if (!normalizedSearch) {
      return true;
    }

    const mergedText = values.filter(Boolean).join(' ');
    const normalizedMergedText = normalizeText(mergedText);
    if (!normalizedSearchTokens.length) {
      return true;
    }
    return normalizedSearchTokens.every((token) =>
      tokenMatchesInNormalizedText(normalizedMergedText, token)
    );
  };

  /** Single-word role queries: narrow to that topic so "employer" does not match job FAQs that only say "employers" in passing. */
  const employerOnlyIntent =
    normalizedSearchTokens.length === 1 &&
    ['employer', 'employers', 'recruiter', 'recruiters'].includes(normalizedSearchTokens[0]);

  /** While searching, scan all FAQ categories (ignore pills). Exception: employer-only intent uses employers category only. */
  const filteredFAQs = employerOnlyIntent
    ? faqs.filter((faq) => faq.category === 'employers')
    : normalizedSearch || activeCategory === 'all'
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  const searchedFAQs = filteredFAQs.filter((faq) =>
    matchesSearch(
      faq.question,
      faq.answer,
      faq.category,
      categorySearchKeywords[faq.category] || ''
    )
  );

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const filterVideos = (videos, roleKey) =>
    (videos || []).filter((v) =>
      matchesSearch(v.title, v.description, roleKey, videoRoleKeywords[roleKey] || '')
    );
  const searchedFeaturedVideo =
    !normalizedSearch || matchesSearch(
      featuredVideo.title,
      featuredVideo.description,
      featuredVideo.category,
      videoRoleKeywords.overview
    )
      ? featuredVideo
      : null;
  const searchedVideoTutorials = {
    jobSeeker: filterVideos(videoTutorials.jobSeeker, 'jobSeeker'),
    employer: filterVideos(videoTutorials.employer, 'employer'),
    intern: filterVideos(videoTutorials.intern, 'intern')
  };
  const anyVideosMatch =
    Boolean(searchedFeaturedVideo) ||
    searchedVideoTutorials.jobSeeker.length > 0 ||
    searchedVideoTutorials.employer.length > 0 ||
    searchedVideoTutorials.intern.length > 0;

  return (
    <div className="help_center_wrapper">
      {/* Back to Home Button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="back-to-home-btn"
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.3s ease',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
      >
        <FontAwesomeIcon icon={faHome} />
        Back to Home
      </button>

      {/* Hero Section */}
      <section className="help_hero">
        <div className="help_hero_container">
          <div className="hero_content">
            <h1 className="hero_title">
              <FontAwesomeIcon icon={faLifeRing} />
              Help Center
            </h1>
            <p className="hero_subtitle">
              Find answers to common questions, get support, and learn how to make the most of AksharJobs.
            </p>
            
            {/* Search Bar */}
            <form
              className="help_search"
              onSubmit={(e) => e.preventDefault()}
              role="search"
              aria-label="Help Center search"
            >
              <input
                type="text"
                placeholder="Search for help topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search_input"
              />
              <button type="submit" className="search_btn" aria-label="Search">
                <FontAwesomeIcon icon={faSearch} />
                <span>Search</span>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Video Tutorials Section */}
      <section className="help-videos">
        <div className="help_container">
          <h2 className="help-videos-title">
            <FontAwesomeIcon icon={faVideo} />
            Video Tutorials
          </h2>
          <p className="help-videos-subtitle">Learn how to use AksharJobs step by step.</p>

          {/* Featured Video: How AksharJobs Works */}
          {searchedFeaturedVideo ? (
            <div className="help-videos-featured">
              <h3 className="help-videos-featured-label">How AksharJobs Works</h3>
              <p className="help-videos-featured-desc">{searchedFeaturedVideo.description}</p>
              <div className="video-card video-card-featured">
                <div className="video-card-embed">
                  {hasVideoId(searchedFeaturedVideo.videoId) ? (
                    <iframe
                      src={getEmbedUrl(searchedFeaturedVideo.videoId.trim())}
                      title={searchedFeaturedVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="video-placeholder">
                      <FontAwesomeIcon icon={faVideo} className="video-placeholder-icon" />
                      <span>Video coming soon</span>
                    </div>
                  )}
                </div>
                <h4 className="video-card-title">{searchedFeaturedVideo.title}</h4>
                <p className="video-card-desc">{searchedFeaturedVideo.description}</p>
              </div>
            </div>
          ) : null}

          {!videosLoading && normalizedSearch && !anyVideosMatch ? (
            <div className="no_results no_results--videos">
              <FontAwesomeIcon icon={faVideo} />
              <p>No videos found matching your search.</p>
            </div>
          ) : null}

          {/* Job Seeker */}
          {searchedVideoTutorials.jobSeeker.length > 0 ? (
            <div className="help-videos-category">
              <h3 className="help-videos-category-title">Job Seeker</h3>
              <div className="video-grid">
                {searchedVideoTutorials.jobSeeker.map((video) => (
                  <div key={video.id} className="video-card">
                    <div className="video-card-embed">
                      {hasVideoId(video.videoId) ? (
                        <iframe
                          src={getEmbedUrl(String(video.videoId).trim())}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="video-placeholder">
                          <FontAwesomeIcon icon={faVideo} className="video-placeholder-icon" />
                          <span>Coming soon</span>
                        </div>
                      )}
                    </div>
                    <h4 className="video-card-title">{video.title}</h4>
                    <p className="video-card-desc">{video.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Employer */}
          {searchedVideoTutorials.employer.length > 0 ? (
            <div className="help-videos-category">
              <h3 className="help-videos-category-title">Employer</h3>
              <div className="video-grid">
                {searchedVideoTutorials.employer.map((video) => (
                  <div key={video.id} className="video-card">
                    <div className="video-card-embed">
                      {hasVideoId(video.videoId) ? (
                        <iframe
                          src={getEmbedUrl(String(video.videoId).trim())}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="video-placeholder">
                          <FontAwesomeIcon icon={faVideo} className="video-placeholder-icon" />
                          <span>Coming soon</span>
                        </div>
                      )}
                    </div>
                    <h4 className="video-card-title">{video.title}</h4>
                    <p className="video-card-desc">{video.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Interns */}
          {searchedVideoTutorials.intern.length > 0 ? (
            <div className="help-videos-category">
              <h3 className="help-videos-category-title">Interns</h3>
              <div className="video-grid">
                {searchedVideoTutorials.intern.map((video) => (
                  <div key={video.id} className="video-card">
                    <div className="video-card-embed">
                      {hasVideoId(video.videoId) ? (
                        <iframe
                          src={getEmbedUrl(String(video.videoId).trim())}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="video-placeholder">
                          <FontAwesomeIcon icon={faVideo} className="video-placeholder-icon" />
                          <span>Coming soon</span>
                        </div>
                      )}
                    </div>
                    <h4 className="video-card-title">{video.title}</h4>
                    <p className="video-card-desc">{video.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq_section">
        <div className="help_container">
          <h2 className="section_title">Frequently Asked Questions</h2>
          
          {/* Category Filter */}
          <div className="faq_filters">
            {faqCategories.map(category => (
              <button
                key={category.id}
                className={`faq_filter ${(!normalizedSearch ? activeCategory === category.id : employerOnlyIntent ? category.id === 'employers' : category.id === 'all') ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSearchTerm('');
                }}
              >
                <FontAwesomeIcon icon={category.icon} />
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          {loading ? (
            <div className="faq_loading" style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ marginBottom: '1rem', display: 'block' }} />
              <p>Loading FAQs...</p>
            </div>
          ) : (
            <div className="faq_list">
              {searchedFAQs.length === 0 ? (
                <div className="no_results">
                  <FontAwesomeIcon icon={faQuestionCircle} />
                  <p>No FAQs found matching your search.</p>
                </div>
              ) : (
                searchedFAQs.map(faq => (
                  <div key={faq.id} className={`faq_item ${openFAQ === faq.id ? 'open' : ''}`}>
                    <button 
                      className="faq_question"
                      onClick={() => toggleFAQ(faq.id)}
                    >
                      <span>{faq.question}</span>
                      <FontAwesomeIcon 
                        icon={openFAQ === faq.id ? faChevronUp : faChevronDown} 
                        className="faq_icon"
                      />
                    </button>
                    <div className="faq_answer">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Support Options */}
      <section className="support_options_section">
        <div className="help_container">
          <h2 className="section_title">Get Support</h2>
          <div className="support_grid">
            {supportOptions.map((option, index) => (
              <div key={index} className="support_card">
                <div className="support_icon">
                  <FontAwesomeIcon icon={option.icon} />
                </div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                <div className="support_details">
                  <span className="availability">{option.availability}</span>
                  <span className="response_time">{option.responseTime}</span>
                </div>
                <button 
                  className="support_btn"
                  onClick={option.action}
                  type="button"
                >
                  Contact Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
